import {
  useState,
  useEffect,
} from 'react';
import {
  useNavigate,
} from 'react-router';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
} from '@stripe/react-stripe-js';
import {
  toast,
  ToastContentProps,
} from 'react-toastify';

import {
  coreActions,
  coreSelectors,
  type Coupon,
  type SubscriptionPlan,
} from '@gymcloud/core';

import config from 'web-config';

import {
  uiActions,
} from 'web-actions';

import {
  getUiState,
} from 'web-selectors';

import {
  Spinner,
  AddCardForm,
  CouponForm,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';
import {
  trackAnalyticsEvent,
} from 'web-utils';

import CardsList from './CardsList';
import { type CouponState } from '../CouponForm/CouponForm';
import Checkout from './Checkout';
import ConfirmationModal from './ConfirmationModal';
import PaymentSuccessfulModal from './PaymentSuccessfulModal';
import SubscriptionNextPaymentInfo from './SubscriptionNextPaymentInfo/';

import * as S from './styled';
import PlanItem from './PlanItem';

const stripePromise = loadStripe(config.stripe.key);

const mapState = (state => ({
  plans: coreSelectors.plansSelector(state) as SubscriptionPlan[],
  profile: coreSelectors.getProfile(state),
  subscriptions: coreSelectors.getSubscriptions(state),
  defaultCardId: coreSelectors.defaultCardIdSelector(state),
  requestStates: coreSelectors.getPaymentRequestsState(state) as any,
  paymentMethods: coreSelectors.getResourceMappedList(
    'paymentMethods',
  )(state),
  lastFailedInvoice: coreSelectors.getLastFailedInvoice(state),
  namespace: getUiState('namespace')(state),
}));

const freePlan = {
  id: '0',
  isFree: true,
  name: 'Program Builder',
  interval: 'Included with IDEA membership',
} as any

interface Props {
  required?: boolean,
}

const IdeafitPayments = ({ required = false }: Props) => {
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [couponState, setCouponState] = useState<CouponState>({ value: '' });
  const [checkoutIsOpen, setCheckoutIsOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>();
  const [addCardFormIsShown, setAddCardForm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [paymentSuccessfulModal, setPaymentSuccessfulModal] = useState(false);
  const [shouldShowPopup, setShouldShowPopup] = useState(false);

  const {
    plans,
    profile,
    namespace,
    subscriptions,
    defaultCardId,
    requestStates,
    paymentMethods,
    lastFailedInvoice,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  const subscription = subscriptions[0] // TODO fix
  const pastDue = subscription?.status === 'past_due';
  const trialing = subscription?.status === 'trialing';

  useEffect(() => {
    setShouldShowPopup(profile.subscription_status === null)
  }, [])

  useEffect(() => {
    dispatch(
      coreActions.requestCurrentCustomer(),
    )

    dispatch(
      coreActions.requestSubscriptionPlans({
        namespace: namespace.symbol,
      }),
    );

    dispatch(
      coreActions.requestGetPaymentMethods({
        callbacks: {
          onSuccess: (res: any[]) => {
            if (res.length === 0) {
              // setAddCardForm(true);
            }
          },
        },
      }),
    );
  }, []);

  const failedWarn = () => {
    toast.info('Any failed payments will be retried with your new card and app content will be updated shortly afterward.');
  };

  const handlePaymentAttempt = () => {
    dispatch(
      coreActions.requestPaymentAttempt({
        onSuccess: () => {
          setPaymentSuccessfulModal(true);
        },
      }),
    );
  };

  const handleAddCard = (res: any, actions: any) => {
    const afterCardAdd = () => {
      if (subscriptions[0]?.noSaved) {
        dispatch(
          coreActions.requestSetPlan({
            body: subscriptions[0].noSaved,
            callbacks: {
              onSuccess: () => {
                if (required) {
                  setPaymentSuccessfulModal(true);
                }
                if (trialing) {
                  setConfirmationModal(true);
                }
                setAddCardForm(false);
              },
            },
          }),
        );
      } else if (!profile?.id || lastFailedInvoice) {
        dispatch(
          coreActions.requestPaymentAttempt({
            onSuccess: () => {
              setPaymentSuccessfulModal(true);
              setAddCardForm(false);
            },
          }),
        );
      } else {
        setAddCardForm(false);
      }
    };

    if (paymentMethods.length === 0 && pastDue) failedWarn();

    dispatch(
      coreActions.requestCreatePaymentMethod({
        qs: {
          continue_subscription: pastDue,
        },
        body: {
          id: res.paymentMethod.id,
        },
        callbacks: {
          ...actions,
          onSuccess: afterCardAdd,
        },
      }),
    );
  };

  const onDeletePaymentMethod = (method: any) => {
    dispatch(
      coreActions.requestDeletePaymentMethod({
        id: method.id,
      }),
    );
  };

  const onSetDefaultPaymentMethod = (method: any) => {
    dispatch(
      coreActions.requestSetDefaultPaymentMethod({
        id: method.id,
      }),
    );
  };

  const handleCancelSubscription = (id: number) => {
    const onSuccess = () => {
      dispatch(
        uiActions.confirmModal({
          modal: {
            title: 'Subscription Cancelled',
            bodyText: 'You will be able to access your premium subscription content until the scheduled date of your next payment. At that time, your account will be downgraded to Freemium status and you will be able to access only your free content.',
            hideCancel: true,
            confirmText: 'Ok',
          },
        }),
      );
    }

    const onConfirmAction = coreActions.requestCancelSubscription({
      id,
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Cancel Subscription',
          bodyText: 'Are you sure you want to cancel your premium subscription?',
          cancelText: 'No, I changed my mind!',
        },
        onSuccess,
        onConfirmAction,
      }),
    );
  };

  const handleCheckCoupon = (coupon: string) => {
    setCouponState(s => ({
      ...s,
      loading: true,
    }))
    dispatch(
      coreActions.requestCheckCoupon({
        coupon,
        callbacks: {
          onSuccess: (res: Coupon) => {
            setCoupon(res)
            setCouponState(s => ({
              ...s,
              loading: false,
              status: 'valid',
            }))
          },
          onError: (res: Coupon) => {
            setCoupon(res)
            setCouponState(s => ({
              ...s,
              loading: false,
              status: 'invalid',
            }))
          },
        },
      }),
    );
  }

  const openComparePlanModal = () => {
    trackAnalyticsEvent('clicked_learn_more')
    dispatch(
      uiActions.setUiState({
        comparePlanModal: {
          isOpen: true,
          isUpgradeable: false,
        },
      }),
    );
  }

  const isLoading = (requestStates.getCustomerState === 'pending'
    || requestStates.setPlanState === 'pending'
    || requestStates.paymentAttemptState === 'pending'
  )

  function UpgradedNotification({ closeToast }: ToastContentProps) {
    return (
      <Flex column spacing={16}>
        <Text color="#000000" size="22px" weight="bold" as="h3">You’ve been upgraded! 🎉</Text>
        <Text color="#000000" size="16px">
          Check out the dashboard tutorial to learn more
          about your premium features.
        </Text>
        <S.PrimaryButton
          onClick={() => {
            closeToast()
            navigate('/welcome');
          }}
        >
          View Tutorial
        </S.PrimaryButton>
      </Flex>
    )
  }

  return (
    <>
      {isLoading && <Spinner over />}

      <Flex
        align="stretch"
        padding="15px"
        width="100%"
        flex="1 1 100%"
        style={{ minHeight: '100%' }}
      >
        <Flex
          column
          flex="0 0 55%"
        >
          <S.SectionCaption>
            Subscriptions
          </S.SectionCaption>
          <S.SectionContainer>
            <Flex column spacing={30} width="100%" padding="30px">
              <Flex column spacing={15} width="100%">
                <PlanItem
                  isFree
                  plan={freePlan}
                  isActive={!subscription?.id || subscription?.cancel_at_period_end}
                  isSelected={selectedPlan?.id == '0'}
                  onClick={() => setSelectedPlan(freePlan)}
                  onMore={() => {
                    openComparePlanModal()
                  }}
                />

                {plans.map(plan => (
                  <PlanItem
                    key={plan.id}
                    plan={plan}
                    isCanceled={subscription?.plan?.id == plan.id && subscription?.cancel_at_period_end}
                    isActive={subscription?.plan?.id == plan.id && !subscription?.cancel_at_period_end}
                    isSelected={selectedPlan?.id == plan.id}
                    onClick={() => setSelectedPlan(plan)}
                    onMore={() => {
                      openComparePlanModal()
                    }}
                  />
                ))}
              </Flex>
              <Flex width="100%" justify="center">
                <S.SetPlan
                  isDisabled={!selectedPlan}
                  onClick={() => {
                    if (selectedPlan?.id !== '0') {
                      setCheckoutIsOpen(true)
                    } else if (subscription?.plan?.id && selectedPlan?.id == '0') {
                      handleCancelSubscription(subscription?.id)
                    }
                  }}
                >
                  Select Plan
                </S.SetPlan>
              </Flex>
            </Flex>
            <Flex column width="100%">
              <Flex
                width="100%"
                padding="30px"
                borderTop="1px solid #cccccc"
              >
                <CouponForm
                  couponState={couponState}
                  onSubmit={handleCheckCoupon}
                />
              </Flex>
              <Flex
                width="100%"
                padding="30px"
                borderTop="1px solid #ccc"
              >
                {subscription?.id ? (
                  <Flex align="center" justify="space-between" width="100%">
                    <Flex column>
                      <Text
                        size="24px"
                        color="#343A3D"
                        weight="600"
                      >

                        {subscription?.plan?.name}
                      </Text>
                      <Text
                        size="18px"
                        color="#343A3D"
                      >
                        {`Billed ${subscription.plan.interval === 'year' ? 'yearly' : 'monthly'}`}
                      </Text>
                    </Flex>
                    <Text
                      size="18px"
                      color="#333333"
                    >
                      <Text size="24px" weight="600">
                        ${parseFloat(subscription.plan.amount / 100).toFixed(2)}
                      </Text>
                      {`/${subscription.plan.interval === 'year' ? 'yr' : 'mo'}`}
                    </Text>
                  </Flex>
                ) : (
                  <Flex align="center" justify="space-between" width="100%">
                    <Flex column>
                      <Text
                        size="24px"
                        color="#343A3D"
                        weight="600"
                      >

                        {freePlan.name}
                      </Text>
                      <Text
                        size="18px"
                        color="#343A3D"
                      >
                        {freePlan.interval}
                      </Text>
                    </Flex>
                    <Text
                      size="24px"
                      color="#333333"
                      weight="600"
                    >
                      FREE
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </S.SectionContainer>
        </Flex>
        <Flex
          column
          grow={1}
          style={{ minHeight: '100%' }}
        >
          <S.SectionCaption>
            Payment Methods
          </S.SectionCaption>
          <S.SectionContainer
            style={{ borderLeft: 'none' }}
          >
            <Flex
              column
              grow={1}
              width="100%"
              height="100%"
              align="center"
              justify="space-between"
            >
              {addCardFormIsShown ? (
                <Elements stripe={stripePromise}>
                  <Flex
                    column
                    padding="10px 5%"
                    border="1px solid #c0c0c0"
                    borderRadius="5px"
                    align="center"
                    justify="space-between"
                    margin="30px 15%"
                    width="70%"
                  >
                    <Flex
                      borderBottom="1px solid #808080"
                      width="100%"
                      padding="0 0 10px 0"
                    >
                      <Text
                        size="22px"
                        weight="bold"
                        color="#333"
                      >
                        Credit Card
                      </Text>
                    </Flex>
                    <AddCardForm
                      submitText="Add Card"
                      onCancel={() => setAddCardForm(false)}
                      onSuccess={handleAddCard}
                    />
                  </Flex>
                </Elements>
              ) : (
                <Flex
                  column
                  align="center"
                  justify={paymentMethods.length > 0 ? 'flex-start' : 'center'}
                  width="100%"
                  height="100%"
                  padding="30px"
                  spacing={30}
                >
                  <CardsList
                    defaultCardId={defaultCardId}
                    paymentMethods={paymentMethods}
                    onDelete={onDeletePaymentMethod}
                    onSetDefault={onSetDefaultPaymentMethod}
                  />
                  <S.PrimaryButton
                    onClick={() => {
                      if (subscription?.status === 'past_due') failedWarn();
                      setAddCardForm(true);
                    }}
                  >
                    Add new card
                  </S.PrimaryButton>
                </Flex>
              )}

              <SubscriptionNextPaymentInfo
                subscription={subscription}
                paymentAttempt={handlePaymentAttempt}
                lastFailedInvoice={lastFailedInvoice}
              />
            </Flex>
          </S.SectionContainer>
        </Flex>

        {checkoutIsOpen && selectedPlan?.id && (
          <Checkout
            coupon={coupon}
            couponState={couponState}
            plan={selectedPlan}
            subscriptionId={subscription?.id}
            onCheckCoupon={handleCheckCoupon}
            onRequestClose={(isFinish) => {
              setCheckoutIsOpen(false)

              if (!isFinish || !shouldShowPopup) return

              toast(UpgradedNotification,
                {
                  className: 'Toastify__toast--first-time-subscribes',
                  position: 'top-right',
                  autoClose: 10000,
                }
              )
              setShouldShowPopup(false)
            }}
          />
        )}

        <PaymentSuccessfulModal
          isOpen={paymentSuccessfulModal}
          onRequestClose={() => {
            setPaymentSuccessfulModal(false);
            navigate('/welcome');
          }}
        />

        <ConfirmationModal
          plan={subscriptions[0]?.plan}
          isOpen={confirmationModal}
          periodEnd={subscriptions[0]?.current_period_end}
          onRequestClose={() => {
            setConfirmationModal(false);
          }}
        />
      </Flex>
    </>
  );
};

export default IdeafitPayments;
